import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useValidationStore } from "@/state/validationState";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";

export type ProjectResponseFiltering =
  components["schemas"]["ProjectResponseFiltering"];
export type ReplaceProjectResponseFilteringRequest =
  components["schemas"]["ReplaceProjectResponseFilteringRequest"];
export type ResponseFilteringFloor =
  components["schemas"]["ResponseFilteringFloor"];

/** Read the Project's effective Output Guard signal list and its provenance (#860). */
export function useResponseFilteringQuery(
  projectId: string,
): UseQueryResult<ProjectResponseFiltering> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.projectResponseFiltering(projectId),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/response-filtering", {
          params: { path: { projectId } },
          signal,
        }),
      );
    },
    enabled: projectId.length > 0,
  });
}

/**
 * Replace — or clear — the Project's signal list under its compare-and-set
 * identity. The caller supplies the version it actually edited; this hook
 * never re-reads a token at submit time, which is the whole point of the CAS.
 */
export function useReplaceResponseFilteringMutation(
  projectId: string,
): UseMutationResult<
  ProjectResponseFiltering,
  unknown,
  ReplaceProjectResponseFilteringRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const clearValidation = useValidationStore((state) => state.clear);

  return useMutation({
    async mutationFn(body) {
      return unwrap(
        await client.PUT("/v1/projects/{projectId}/response-filtering", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    async onSuccess(saved) {
      queryClient.setQueryData(
        queryKeys.projectResponseFiltering(projectId),
        saved,
      );
      await settleResponseFilteringWrite(queryClient, projectId, clearValidation);
    },
    onError(error) {
      const failure = classifyResponseFilteringWriteFailure(error);
      // `summaryPresentation`'s rule: a stale refusal PROVES the cached token
      // moved, and a transport/5xx failure is ambiguous — the server may have
      // committed before the reply was lost. Refresh in both cases, and never
      // resend automatically.
      if (failure.kind !== "stale" && failure.kind !== "ambiguous") {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectResponseFiltering(projectId),
      });
      // THE SAME SETTLEMENT AS A SUCCESS, and `settleStaleAdoptions`' reason:
      // a stale refusal is authoritative news that somebody else's write
      // already changed the effective candidate content, and an ambiguous
      // failure may have committed. Either way this tab's DURABLE readings —
      // the `/draft` validation badge the candidate gate falls back to, the
      // Overview, the candidates' freshness — describe a configuration that no
      // longer exists. Clearing only the session report left the gate reading
      // `passed` off the cached badge.
      void settleResponseFilteringWrite(queryClient, projectId, clearValidation);
    },
    retry: false,
  });
}

/**
 * What every mounted reader has to re-observe after the signal list moved.
 *
 * `library.settleAdoptionWrite`'s rule, for the same reason and with the same
 * consequence if it is skipped: this write changes what a freeze would export,
 * so a passed validation report certifies content the report never saw, and a
 * frozen candidate's server-derived freshness no longer describes the Project.
 * Leaving either cached would keep the candidate gate enabled and keep the
 * Preview modal calling a superseded candidate "Current".
 *
 * The projection keys are NOT invalidated: `conversation_agent/output_guard.yaml`
 * is a documented omission from the browser's section partition
 * (`section_registry._UNSECTIONED_EXACT`), so no section's fields, locks or
 * provenance can move because this decision did.
 *
 * Awaited rather than fire-and-forget, as the adoption writer is: the dialog
 * closes when the mutation resolves, and a fire-and-forget invalidation lets it
 * close over the previous reading for a visible interval.
 */
async function settleResponseFilteringWrite(
  queryClient: QueryClient,
  projectId: string,
  clearValidation: (projectId: string) => void,
): Promise<void> {
  clearValidation(projectId);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.draft(projectId) }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.projectOverview(projectId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.previewCandidates(projectId),
    }),
  ]);
}

export type ResponseFilteringWriteFailure =
  | { kind: "stale"; currentFilteringVersion: string | null }
  /** The pinned engine's guard policy locks the document. No list is acceptable. */
  | { kind: "floor_locked"; lockedKeys: string[] }
  /** No usable clinical engine, so no policy authority to validate against. */
  | { kind: "upstream" }
  | { kind: "denied" }
  | { kind: "invalid"; message: string; requestId: string | undefined }
  | { kind: "ambiguous"; requestId: string | undefined }
  | { kind: "error"; code: string; requestId: string | undefined };

function envelopeString(error: ApiError, key: string): string | null {
  const value = error.envelope[key];
  return typeof value === "string" ? value : null;
}

function envelopeStrings(error: ApiError, key: string): string[] {
  const value = error.envelope[key];
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

export function classifyResponseFilteringWriteFailure(
  error: unknown,
): ResponseFilteringWriteFailure {
  if (!(error instanceof ApiError)) {
    return { kind: "ambiguous", requestId: undefined };
  }
  if (error.code === "stale_response_filtering_version") {
    return {
      kind: "stale",
      currentFilteringVersion: envelopeString(
        error,
        "currentResponseFilteringVersion",
      ),
    };
  }
  if (error.code === "response_filtering_floor_locked") {
    return {
      kind: "floor_locked",
      lockedKeys: envelopeStrings(error, "responseFilteringLockedKeys"),
    };
  }
  if (error.isForbidden || error.isUnauthenticated || error.status === 404) {
    return { kind: "denied" };
  }
  if (error.status === 502 || error.status === 503 || error.status === 504) {
    return { kind: "upstream" };
  }
  if (error.status >= 500 && !error.isNotImplemented) {
    return { kind: "ambiguous", requestId: error.requestId };
  }
  if (error.status === 400 || error.status === 413 || error.status === 422) {
    // The SERVER's own sentence, deliberately. Every other refusal on this
    // surface renders client copy from a code, but a 422 here is the typed
    // validator explaining which rule the submitted list broke, and
    // paraphrasing it would either lose that or duplicate the rules.
    return {
      kind: "invalid",
      message: error.message,
      requestId: error.requestId,
    };
  }
  return { kind: "error", code: error.code, requestId: error.requestId };
}
